import {useMutation, useQueryClient} from "@tanstack/react-query";
import {UsersApi} from "@devour/client";
import getConfig from "@/utils/getConfig";
import {addError, decrementLoading, incrementLoading} from "@/redux/meta/metaActions";
import {useDispatch, useSelector} from "react-redux";
import {IStore} from "@/redux/defaultStore";
import {USE_GET_USER_PROFILE_KEY} from "@/hooks/useGetUserProfile";
import {useGetStripePaymentMethodList} from "@/hooks/useGetStripePaymentMethodList";

/**
 * Finalize a card that was already collected + verified via a SetupIntent
 * (stripe.confirmCardSetup attaches the verified PaymentMethod to the customer).
 * This sets it as the user's default and refreshes the cached lists. The raw
 * card number/CVC never reach our backend — see CardPaymentInputs / getStripePromise.
 */
export function useCreateStripePaymentMethod() {
    const dispatch = useDispatch();
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const queryClient = useQueryClient();
    const {refetch: refetchStripePaymentMethodListData} = useGetStripePaymentMethodList(fullToken);

    return useMutation({
        mutationFn: async (paymentMethodId: string): Promise<void> => {
            dispatch(incrementLoading());
            try {
                await refetchStripePaymentMethodListData();

                await new UsersApi(getConfig(fullToken)).updateUsersUpdateDefaultPayment({
                    defaultPaymentMethodId: paymentMethodId,
                });

                queryClient.invalidateQueries({queryKey: [USE_GET_USER_PROFILE_KEY]});
            } finally {
                dispatch(decrementLoading());
            }
        },
        onError: async (e) => {
            dispatch(await addError(e as any));
        },
    });
}
